import type {
  ConsumptionProfitDistributionParams,
  ConsumptionProfitDistributionResponse,
  DistributionDayData,
  DistributionDetailParams,
  DistributionDetailResponse,
  MonthlySpendProfitResponse,
} from './types/consumption-profit-distribution'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { dayjs } from '#/utils'

export const consumptionProfitDistributionApi = defineApi(
  'operation',
  ({ prefix }) => {
    /**
     * 消耗、利润分布
     * POST /gobestads/operation/customerFundChangeDaily/queryDistribution
     */
    function getDistribution(
      params: ConsumptionProfitDistributionParams,
    ): Promise<DistributionDayData[]> {
      const dateRange = params.dateRange.map((date) =>
        dayjs(date).format('YYYY-MM-DD'),
      )

      return requestClient
        .post<ConsumptionProfitDistributionResponse>(
          prefix('customerFundChangeDaily/queryDistribution'),
          {
            dateRange,
            type: params.type,
          },
        )
        .then((res) => {
          // requestClient 已提取 data 字段，res 实际是 DistributionDayData[]
          return ((res as unknown as DistributionDayData[]) || []).map(
            (item) => ({
              date: item.date,
              items: item.items.map((item) => ({
                accountType: item.accountType,
                amount: item.amount || 0,
                percent: item.percent || 0,
              })),
            }),
          ) as DistributionDayData[]
        })
    }

    /**
     * 消耗、利润分布详情
     * POST /gobestads/operation/customerFundChangeDaily/queryDistributionDetail
     */
    function getDistributionDetail(params: DistributionDetailParams) {
      return requestClient
        .post<DistributionDetailResponse>(
          prefix('customerFundChangeDaily/queryDistributionDetail'),
          {
            date: dayjs(params.date).format('YYYY-MM-DD'),
            type: params.type,
          },
        )
        .then((res) => {
          // requestClient 已提取 data 字段，res 实际是 DistributionDetailResponse['data']
          const data =
            (res as unknown as DistributionDetailResponse['data']) || {
              top20RiseCustomers: [],
              top20DeclineCustomers: [],
              top20RiseAccounts: [],
              top20DeclineAccounts: [],
            }

          // 过滤和格式化数据，确保每个字段都有正确的类型和默认值
          return {
            top20RiseCustomers:
              data.top20RiseCustomers?.map((item) => ({
                customerId: item.customerId,
                companyName: item.companyName || '',
                accountId: item.accountId || '',
                accountName: item.accountName || '',
                operatorOpenId: item.operatorOpenId,
                managerOpenId: item.managerOpenId || '',
                changeAmount: item.changeAmount || 0,
                changePercent: item.changePercent || 0,
                amount: item.amount,
              })) || [],
            top20DeclineCustomers:
              data.top20DeclineCustomers?.map((item) => ({
                customerId: item.customerId,
                companyName: item.companyName || '',
                accountId: item.accountId || '',
                accountName: item.accountName || '',
                operatorOpenId: item.operatorOpenId,
                managerOpenId: item.managerOpenId || '',
                changeAmount: item.changeAmount || 0,
                changePercent: item.changePercent || 0,
                amount: item.amount,
              })) || [],
            top20RiseAccounts:
              data.top20RiseAccounts?.map((item) => ({
                customerId: item.customerId,
                companyName: item.companyName || '',
                accountId: item.accountId || '',
                accountName: item.accountName || '',
                operatorOpenId: item.operatorOpenId,
                managerOpenId: item.managerOpenId || '',
                changeAmount: item.changeAmount || 0,
                changePercent: item.changePercent || 0,
                amount: item.amount,
              })) || [],
            top20DeclineAccounts:
              data.top20DeclineAccounts?.map((item) => ({
                customerId: item.customerId,
                companyName: item.companyName || '',
                accountId: item.accountId || '',
                accountName: item.accountName || '',
                operatorOpenId: item.operatorOpenId,
                managerOpenId: item.managerOpenId || '',
                changeAmount: item.changeAmount || 0,
                changePercent: item.changePercent || 0,
                amount: item.amount,
              })) || [],
          }
        })
    }

    /**
     * 查询月消耗、利润总额统计
     * POST /gobestads/operation/customerFundChangeDaily/queryMonthlySpendProfit
     */
    function getMonthlySpendProfit() {
      return requestClient
        .post<MonthlySpendProfitResponse>(
          prefix('customerFundChangeDaily/queryMonthlySpendProfit'),
          {},
        )
        .then((res) => {
          // requestClient 已提取 data 字段，res 实际是 MonthlySpendProfitResponse
          const data = (res as unknown as MonthlySpendProfitResponse) || {}

          // 过滤和格式化数据，确保每个字段都有正确的类型和默认值
          return {
            lastMonthSpend: data.lastMonthSpend ?? 0,
            currentMonthSpend: data.currentMonthSpend ?? 0,
            timeProgress: data.timeProgress ?? 0,
            spendCatchUpProgress: data.spendCatchUpProgress ?? 0,
            lastMonthProfit: data.lastMonthProfit ?? 0,
            currentMonthProfit: data.currentMonthProfit ?? 0,
            profitCatchUpProgress: data.profitCatchUpProgress ?? 0,
          }
        })
    }

    return {
      getDistribution,
      getDistributionDetail,
      getMonthlySpendProfit,
    }
  },
)
